<script setup lang="ts">
import { promiseTimeout } from "@vueuse/core";
import { refuge } from "~/data/refuge";

// Shared with app.vue's schema.org graph, which used to redeclare the same
// address and number a few lines away from these.
const email = refuge.contact.email
const emailHref = `mailto:${email}`
const phone = refuge.contact.phone
const phoneHref = `tel:${refuge.contact.phoneE164}`
const facebookUrl = refuge.social.facebook
const instagramUrl = refuge.social.instagram

const copied = ref(false);

async function copyEmail(): Promise<void> {
  await navigator.clipboard.writeText(email);
  copied.value = true;
  await promiseTimeout(2000);
  copied.value = false;
}
</script>

<template>
  <div class="contact-information">
    <div class="contact-grid">
      <!-- Phone Card -->
      <a :href="phoneHref" class="contact-card">
        <div class="icon-wrapper">
          <Icon name="ph:phone" size="1.4rem" />
        </div>
        <div class="card-content">
          <span class="label">{{ $t('phone') }}</span>
          <span class="value">{{ phone }}</span>
        </div>
      </a>

      <!-- Email Card -->
      <div class="contact-card email-card">
        <a :href="emailHref" class="main-link">
          <div class="icon-wrapper">
            <Icon name="ph:envelope-simple" size="1.4rem" />
          </div>
          <div class="card-content">
            <span class="label">{{ $t('email') }}</span>
            <span class="value">{{ email }}</span>
          </div>
        </a>
        <button 
          class="copy-btn" 
          @click="copyEmail" 
          :aria-label="$t('copyToClipboard')"
          :class="{ 'is-copied': copied }"
        >
          <Icon :name="copied ? 'ph:check' : 'ph:copy'" size="1rem" aria-hidden="true"/>
          <Transition name="fade">
            <span v-if="copied" class="copy-feedback" role="status" aria-live="polite">{{ $t('copied') }}</span>
          </Transition>
        </button>
      </div>

      <!-- Socials -->
      <a :href="facebookUrl" target="_blank" rel="noopener noreferrer" class="contact-card">
        <div class="icon-wrapper">
          <Icon name="ph:facebook-logo" size="1.4rem" />
        </div>
        <div class="card-content">
          <span class="label">{{ $t('facebook') }}</span>
          <span class="value">@KatarraktisRefuge</span>
        </div>
      </a>

      <a :href="instagramUrl" target="_blank" rel="noopener noreferrer" class="contact-card">
        <div class="icon-wrapper">
          <Icon name="ph:instagram-logo" size="1.4rem" />
        </div>
        <div class="card-content">
          <span class="label">{{ $t('instagram') }}</span>
          <span class="value">@katarraktis.refuge</span>
        </div>
      </a>
    </div>
  </div>
</template>

<style scoped lang="sass">
@use "@/assets/sass/_variables" as *

.contact-information
  width: 100%
  padding: 1rem 0
  margin: 0.5rem 0

.contact-grid
  display: grid
  grid-template-columns: repeat(4, 1fr)
  gap: 0.75rem
  width: 100%

.contact-card
  display: flex
  align-items: center
  gap: 0.75rem
  padding: 0.85rem
  background: rgba(0, 0, 0, 0.03)
  border: 1px solid rgba(0, 0, 0, 0.08)
  border-radius: $border-radius-card
  text-decoration: none
  color: inherit
  transition: all $transition-standard
  position: relative
  overflow: hidden

  &:hover
    background: rgba(0, 0, 0, 0.05)
    border-color: $color-primary
    transform: translateY(-2px)
    box-shadow: $shadow-modern

    .icon-wrapper
      background: $color-primary
      color: white

.dark-mode .contact-card
  background: rgba(255, 255, 255, 0.03)
  border-color: rgba(255, 255, 255, 0.1)

  &:hover
    background: rgba(255, 255, 255, 0.05)

.icon-wrapper
  display: flex
  align-items: center
  justify-content: center
  width: 2.75rem
  height: 2.75rem
  border-radius: 0.85rem
  background: rgba(0, 0, 0, 0.05)
  color: $color-primary
  transition: all $transition-standard
  flex-shrink: 0

.dark-mode .icon-wrapper
  background: rgba(255, 255, 255, 0.05)

.card-content
  display: flex
  flex-direction: column
  gap: 0.25rem
  text-align: left
  min-width: 0

.label
  font-size: 0.8rem
  text-transform: uppercase
  letter-spacing: 0.05em
  font-weight: 700
  opacity: 0.7

.value
  font-size: 1rem
  font-weight: 600
  word-break: break-all
  line-height: 1.2

// Email card special handling
.email-card
  padding-right: 3rem
  cursor: default
  
  &:hover
    transform: none
    box-shadow: none
    background: rgba(0, 0, 0, 0.03)
    border-color: rgba(0, 0, 0, 0.08)

.dark-mode .email-card:hover
  background: rgba(255, 255, 255, 0.03)
  border-color: rgba(255, 255, 255, 0.1)

.main-link
  display: flex
  align-items: center
  gap: 0.75rem
  text-decoration: none
  color: inherit
  flex: 1
  min-width: 0
  
  &:hover
    .icon-wrapper
      background: $color-primary
      color: white
    .value
      color: $color-primary

.copy-btn
  position: absolute
  right: 0.5rem
  top: 50%
  transform: translateY(-50%)
  background: none
  border: none
  padding: 0.5rem
  cursor: pointer
  color: inherit
  opacity: 0.4
  transition: all $transition-fast
  display: flex
  flex-direction: column
  align-items: center
  gap: 0.15rem
  z-index: 2

  &:hover
    opacity: 1
    color: $color-primary
  
  &.is-copied
    opacity: 1
    color: #10b981

.copy-feedback
  position: absolute
  bottom: -0.4rem
  font-size: 0.55rem
  font-weight: 800
  text-transform: uppercase
  white-space: nowrap

.fade-enter-active, .fade-leave-active
  transition: opacity 0.2s ease

.fade-enter-from, .fade-leave-to
  opacity: 0

@media (max-width: 1024px)
  .contact-grid
    grid-template-columns: repeat(2, 1fr)
    gap: 0.75rem

@media (max-width: 600px)
  .contact-information
    padding: 0.5rem 0
    
  .contact-grid
    gap: 0.5rem
    
  .contact-card
    padding: 1rem 0.75rem
    gap: 0.75rem
    flex-direction: column
    align-items: center
    text-align: center
    
  .icon-wrapper
    width: 2.5rem
    height: 2.5rem
    
  .card-content
    align-items: center
    text-align: center

  .label
    font-size: 0.85rem

  .value
    font-size: 1.05rem
    
  .email-card
    padding: 1rem 0.75rem
    
  .main-link
    flex-direction: column
    align-items: center
    width: 100%
    gap: 0.75rem
    
  .copy-btn
    position: relative
    top: auto
    right: auto
    transform: none
    padding: 0.4rem
    opacity: 0.6
    
  .copy-feedback
    bottom: -0.8rem
    left: 50%
    transform: translateX(-50%)
    right: auto
</style>
